import { useState } from "react";
import { Plus } from "lucide-react";
import { SkillEditorMode, type SkillFormData } from "~/components/assistant/SkillEditorForm";
import SkillEditorModal from "~/components/assistant/SkillEditorModal";
import { SkillRow } from "~/components/assistant/SkillRow";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import useUser from "~/contexts/user";
import { ConfirmDestructiveDialog } from "~/pages/manifold-nextgen/components/ConfirmDestructiveDialog";
import { useSkillMutations } from "~/pages/manifold-nextgen/components/conversations/skills/useSkillMutations";
import { SettingsSection } from "~/pages/manifold-nextgen/components/settings/SettingsSection";
import { type PartialSkill, type Skill } from "~/services/skills";
import { useUserSkills } from "./useUserSkills";

type SkillDialogState = { kind: "none" } | { kind: "editor"; skill?: Skill };

/**
 * The profile's Skills tab: the user's personal USER-scope skill library, which
 * every conversation they start can draw on.
 */
export const ProfileSkillsTab = () => {
  const user = useUser();
  const { showSuccess, showError } = useToast();

  const { skills, isLoading: skillsLoading } = useUserSkills(user?.email);
  const { createUserSkill, updateSkill, deleteSkill, getSkill } = useSkillMutations();

  const [skillDialog, setSkillDialog] = useState<SkillDialogState>({ kind: "none" });
  const [deleteTarget, setDeleteTarget] = useState<PartialSkill | null>(null);
  const [isSavingSkill, setIsSavingSkill] = useState(false);

  const handleEditSkill = async (skillId: string) => {
    try {
      const skill = await getSkill(skillId);
      setSkillDialog({ kind: "editor", skill });
    } catch {
      showError("Unable to load skill for editing");
    }
  };

  const handleSkillSubmit = async (data: SkillFormData) => {
    setIsSavingSkill(true);
    try {
      if (skillDialog.kind === "editor" && skillDialog.skill) {
        await updateSkill(skillDialog.skill.id, {
          description: data.description,
          content: data.content,
        });
        showSuccess(`"${skillDialog.skill.name}" updated`);
      } else {
        await createUserSkill(data);
        showSuccess(`"${data.name}" added`);
      }
      setSkillDialog({ kind: "none" });
    } catch (e) {
      showError(e instanceof Error ? e.message : "Unable to save skill");
    } finally {
      setIsSavingSkill(false);
    }
  };

  const handleDeleteSkillConfirm = async (skill: PartialSkill) => {
    try {
      await deleteSkill(skill);
      showSuccess(`"${skill.name}" removed`);
    } catch (e) {
      showError("Unable to remove skill");
      throw e;
    }
  };

  const skillEditorInitialData =
    skillDialog.kind === "editor" && skillDialog.skill
      ? {
          name: skillDialog.skill.name,
          description: skillDialog.skill.description ?? "",
          content: skillDialog.skill.content,
        }
      : undefined;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <SettingsSection
        title="Skills"
        description="These skills are available in all your conversations."
        headerAction={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSkillDialog({ kind: "editor" })}
            className="text-primary hover:text-primary/80"
          >
            <Plus />
            Add skill
          </Button>
        }
      >
        {skillsLoading ? (
          <div className="flex flex-col gap-2 p-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : skills.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className="text-sm text-muted-foreground">No skills yet.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {skills.map((skill) => (
              <SkillRow
                key={skill.id}
                skill={skill}
                canEdit
                onEdit={() => handleEditSkill(skill.id)}
                onDelete={() => setDeleteTarget(skill)}
              />
            ))}
          </ul>
        )}
      </SettingsSection>

      <SkillEditorModal
        open={skillDialog.kind === "editor"}
        onClose={() => setSkillDialog({ kind: "none" })}
        onSubmit={handleSkillSubmit}
        loading={isSavingSkill}
        mode={
          skillDialog.kind === "editor" && skillDialog.skill
            ? SkillEditorMode.EDIT
            : SkillEditorMode.CREATE
        }
        initialData={skillEditorInitialData}
      />

      <ConfirmDestructiveDialog
        target={deleteTarget}
        title="Remove this skill?"
        description={(skill) => <>&ldquo;{skill.name}&rdquo; will be permanently deleted.</>}
        confirmLabel="Remove"
        pendingLabel="Removing…"
        onConfirm={handleDeleteSkillConfirm}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
};
